import { useEffect, useState } from "react"
import { FaUpload } from "react-icons/fa6"
import { Box, Center, CloseButton, Dialog, HStack, Icon, Portal, Spinner, Stack, VStack } from "@chakra-ui/react"
import { Button, Text } from "@/components/ui"
import { DropZone } from "@/features/documents/components/DropZone"

const WHITE_LISTED_FILE_TYPES = [
  "image/png",
  "image/jpeg",
  "image/jpg",
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/msword"
]

interface MiscUploadModalProps {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  onUpload: (file: File, customName: string) => void
  isLoading: boolean
  isSuccess: boolean
}

const stripExtension = (name: string): string => {
  const lastDot = name.lastIndexOf(".")
  return lastDot > 0 ? name.slice(0, lastDot) : name
}

export const MiscUploadModal = ({ isOpen, onOpenChange, onUpload, isLoading, isSuccess }: MiscUploadModalProps) => {
  const [file, setFile] = useState<File | null>(null)
  const [customName, setCustomName] = useState("")
  const [error, setError] = useState("")

  useEffect(() => {
    if (isSuccess) {
      setFile(null)
      setCustomName("")
      setError("")
    }
  }, [isSuccess])

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setFile(null)
      setCustomName("")
      setError("")
    }
  }, [isOpen])

  const handleFileSelect = (selectedFile: File) => {
    if (!WHITE_LISTED_FILE_TYPES.includes(selectedFile.type)) {
      setError(`${selectedFile.type || "Unknown file type"} is not supported. Please use PDF, PNG, JPG, DOC, or DOCX.`)
      return
    }
    setError("")
    setFile(selectedFile)
    setCustomName(stripExtension(selectedFile.name))
  }

  const handleFilesDrop = (files: File[]) => {
    if (files.length > 0) handleFileSelect(files[0])
  }

  const handleFilesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return
    handleFileSelect(e.target.files[0])
    e.target.value = ""
  }

  const handleUpload = () => {
    if (!file) return
    const trimmed = customName.trim()
    if (!trimmed) {
      setError("Please enter a file name.")
      return
    }
    onUpload(file, trimmed)
  }

  return (
    <Dialog.Root open={isOpen} onOpenChange={(e) => onOpenChange(e.open)}>
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content p={6}>
            <HStack>
              <Dialog.Title>Upload Miscellaneous Document</Dialog.Title>
              <Dialog.CloseTrigger asChild>
                <CloseButton size="sm" />
              </Dialog.CloseTrigger>
            </HStack>
            <Dialog.Body mt={4}>
              <DropZone onFilesDrop={handleFilesDrop}>
                <VStack pt={10} pb={10}>
                  <Icon as={FaUpload as React.ElementType} boxSize={10} color="black" />
                  <Box className="file-upload-container" mt={5} mb={5}>
                    <label className="file-upload-label">
                      <input
                        type="file"
                        accept=".jpg,.jpeg,.png,.doc,.docx,.pdf"
                        className="file-upload-input"
                        onChange={handleFilesChange}
                      />
                      Choose a file
                    </label>
                  </Box>

                  {!file ? (
                    <Text fontSize="sm">No file selected</Text>
                  ) : (
                    <Stack w="100%" px={4} gap={3}>
                      <Text fontSize="sm" fontWeight="medium">{file.name}</Text>
                      <Stack gap={1}>
                        <Text fontSize="sm" fontWeight="bold">File name</Text>
                        <input
                          type="text"
                          value={customName}
                          onChange={(e) => setCustomName(e.target.value)}
                          style={{
                            width: "100%",
                            padding: "8px 12px",
                            border: "1px solid #E2E8F0",
                            borderRadius: "6px",
                            fontSize: "14px",
                            fontFamily: "Nunito Sans, sans-serif",
                          }}
                        />
                        <Text fontSize="xs" color="gray.500">
                          This will appear in the final file name
                        </Text>
                      </Stack>
                      <HStack pt={2}>
                        <Button variant="solid" onClick={handleUpload}>Upload</Button>
                        <Button variant="outline" onClick={() => { setFile(null); setCustomName(""); setError(""); }}>Clear</Button>
                      </HStack>
                    </Stack>
                  )}

                  {error && (
                    <Text fontSize="sm" color="red.500" mt={2}>{error}</Text>
                  )}

                  {isLoading && (
                    <Center mt={2}><Spinner /></Center>
                  )}
                </VStack>
              </DropZone>
            </Dialog.Body>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  )
}
